import { useSetAnalyzeResultTotalCountAtom } from '@/features/analyze/atoms/analyzeTotalResultCountAtom';
import { filterConditionListener } from '@/features/analyze/atoms/filterCondition.atom';
import {
  usePageConditionAtomValue,
  useSetPageOffsetAtom,
} from '@/features/analyze/atoms/pageCondition.atom';
import { useArticleSearchCondition } from '@/features/analyze/hooks/useArticleSearchCondition';
import { graphql, useQuery, VariablesOf } from '@/lib/graphql';
import { useEffect } from 'react';

// GraphQLクエリ定義
const GET_ARTICLES_QUERY = graphql(`
  query getArticles(
    $condition: ArticleSearchConditionInput!
    $page: Int!
    $perPage: Int!
  ) {
    articles(condition: $condition, page: $page, perPage: $perPage) {
      items {
        id
        code
        title
        publishedAt
        summary
        country
        countryTags
        organizationType
        organization
        imageUrl
        url
        displayableTopicTags {
          lookupKey
          name
          description
          themes
          isSocialIssue
        }
        isBookmarkedByCurrentUser
        themes
        people
        companies
        locations
        bodyPreview
      }
      pageInfo {
        currentPage
        hasNextPage
        hasPreviousPage
        lastPage
        totalCount
        perPage
      }
    }
  }
`);

type ArticlesVariables = VariablesOf<typeof GET_ARTICLES_QUERY>;

const useInitPageOffset = () => {
  const setPageOffset = useSetPageOffsetAtom();
  useEffect(() => {
    const unsubscribe = filterConditionListener(() => {
      setPageOffset(0);
    });

    return unsubscribe;
  }, [setPageOffset]);
};

export const useAnalyzeSearch = () => {
  useInitPageOffset();

  const condition = useArticleSearchCondition();
  const pageCondition = usePageConditionAtomValue();
  const setTotalResultCount = useSetAnalyzeResultTotalCountAtom();

  // GraphQL実装
  const { data, loading, error } = useQuery(GET_ARTICLES_QUERY, {
    variables: {
      condition,
      page:
        Math.floor(
          (pageCondition.offset || 0) / (pageCondition.perPage || 10)
        ) + 1,
      perPage: pageCondition.perPage || 10,
    } satisfies ArticlesVariables,
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (data?.articles.pageInfo?.totalCount !== undefined) {
      setTotalResultCount(data.articles.pageInfo.totalCount);
    }
  }, [data?.articles.pageInfo?.totalCount, setTotalResultCount]);

  // エラーハンドリング
  if (error) {
    console.error('GraphQL Error in useAnalyzeSearch:', error);
  }

  return {
    docs: data?.articles.items,
    pageInfo: data?.articles.pageInfo,
    isLoading: loading,
  };
};
